import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Input, SingleSelect, TrashAlt, Toastify, GripVertical, DatePicker } from 'lift-ui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAutoScroll } from '../hooks/useAutoScroll';
import { BrandConfig, BrandThreshold } from '../types';
import {
  getBorderClasses,
  hasConnectionToNext as checkConnectionToNext,
  hasConnectionFromPrev as checkConnectionFromPrev,
} from '../utils/thresholdFilters';

import { validateThreshold } from './BrandThresholdTable';
import { FilterDropdown } from './FilterDropdown';

// Helper function to get error key for a threshold
const getThresholdErrorKey = (brandId: number, thresholdId: string) => `${brandId}-${thresholdId}`;

// Types for drag-to-fill feature
type FillableColumn = 'taxonomyType' | 'valueType' | 'operator' | 'threshold';

interface DragFillState {
  isActive: boolean;
  sourceRowIndex: number;
  sourceColumn: FillableColumn;
  targetRowIndices: number[];
  mouseY?: number;
}

interface CellSelectionState {
  rowIndex: number;
  column: FillableColumn;
}

// Sortable Threshold Row Component
interface SortableThresholdRowProps {
  threshold: BrandThreshold;
  index: number;
  originalIndex: number;
  isLastRow: boolean;
  hasConnectionToNext: boolean;
  hasConnectionFromPrev: boolean;
  children: React.ReactNode;
  logicalOperatorRow?: React.ReactNode;
}

const SortableThresholdRow: React.FC<SortableThresholdRowProps> = ({
  threshold,
  hasConnectionToNext,
  hasConnectionFromPrev,
  children,
  logicalOperatorRow,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: threshold.id,
  });

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Clone the logical operator row and apply the same transform to it
  const transformedLogicalOperatorRow = logicalOperatorRow
    ? React.cloneElement(
        logicalOperatorRow as React.ReactElement<{ style?: React.CSSProperties }>,
        {
          style: {
            ...((logicalOperatorRow as React.ReactElement<{ style?: React.CSSProperties }>).props
              ?.style || {}),
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
          },
        }
      )
    : null;

  return (
    <React.Fragment>
      <tr ref={setNodeRef} style={rowStyle}>
        {/* Drag Handle Column */}
        <td
          className={`border-l border-r border-gray-300 ${hasConnectionFromPrev ? '' : 'border-t'} ${hasConnectionToNext ? '' : 'border-b'} px-1 py-2 bg-gray-50/50`}
        >
          <div className="flex items-center justify-center">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors duration-150 p-1 rounded hover:bg-gray-100"
              title="Drag to reorder"
            >
              <GripVertical className="h-4 w-4" />
            </button>
          </div>
        </td>
        {children}
      </tr>
      {transformedLogicalOperatorRow}
    </React.Fragment>
  );
};

interface TaxonomyThresholdTableProps {
  brandConfig: BrandConfig;
  onUpdateThreshold: (
    brandId: number,
    thresholdIndex: number,
    field: keyof BrandThreshold,
    value: string | number
  ) => void;
  onAddThresholdRow: (brandId: number) => void;
  onRemoveThresholdRow: (brandId: number, thresholdIndex: number) => void;
  onReorderThresholds: (brandId: number, fromIndex: number, toIndex: number) => void;
  onClearAllThresholds: (brandId: number) => void;
  isLoadingInitialData?: boolean;
  alertTypes?: Array<{
    id: number;
    name: string;
    display_name: string;
    description: string;
    data_source: string;
    created_at: string;
    updated_at: string;
    default_threshold_config?: Array<{
      value: number;
      metric: string;
      operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
      max_value?: string | number;
      period_days?: number;
    }>;
    alert_query_config?: {
      properties: string[];
    };
  }>;
  toolName?: string;
  onUpdateLogicalOperator?: (
    brandId: number,
    thresholdIndex: number,
    operator: 'AND' | 'OR' | 'NONE' | 'ADD'
  ) => void;
}

export const TaxonomyThresholdTable: React.FC<TaxonomyThresholdTableProps> = ({
  brandConfig,
  onUpdateThreshold,
  onAddThresholdRow,
  onRemoveThresholdRow,
  onReorderThresholds,
  onClearAllThresholds,
  isLoadingInitialData,
  alertTypes,
  toolName,
  onUpdateLogicalOperator,
}) => {
  // Determine if inputs should be disabled
  const isDisabled = isLoadingInitialData;

  // Drag-to-fill state
  const [selectedCell, setSelectedCell] = useState<CellSelectionState | null>(null);
  const [dragFillState, setDragFillState] = useState<DragFillState>({
    isActive: false,
    sourceRowIndex: -1,
    sourceColumn: 'taxonomyType',
    targetRowIndices: [],
  });
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const mouseYRef = useRef<number | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  const dragDirectionRef = useRef<'up' | 'down' | null>(null);

  const { startAutoScroll, stopAutoScroll } = useAutoScroll({
    scrollContainerRef,
    isDraggingRef,
    mouseYRef,
    dragDirectionRef,
  });

  const cellRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());
  const [dragOverlay, setDragOverlay] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const getCellRefKey = (rowIndex: number, column: FillableColumn) => `${rowIndex}-${column}`;

  const registerCellRef = useCallback(
    (rowIndex: number, column: FillableColumn, el: HTMLTableCellElement | null) => {
      const key = getCellRefKey(rowIndex, column);
      if (el) {
        cellRefs.current.set(key, el);
      } else {
        cellRefs.current.delete(key);
      }
    },
    []
  );

  // Update overlay position when drag state changes
  useEffect(() => {
    if (!dragFillState.isActive) {
      setDragOverlay(null);
      return;
    }
    const sourceKey = getCellRefKey(dragFillState.sourceRowIndex, dragFillState.sourceColumn);
    const sourceCell = cellRefs.current.get(sourceKey);
    if (!sourceCell || !tableRef.current) {
      setDragOverlay(null);
      return;
    }
    const tableRect = tableRef.current.getBoundingClientRect();
    const sourceRect = sourceCell.getBoundingClientRect();
    if (dragFillState.targetRowIndices.length === 0) {
      setDragOverlay({
        top: sourceRect.top - tableRect.top,
        left: sourceRect.left - tableRect.left,
        width: sourceRect.width,
        height: sourceRect.height,
      });
      return;
    }
    const firstTargetIndex = dragFillState.targetRowIndices[0];
    const lastTargetIndex =
      dragFillState.targetRowIndices[dragFillState.targetRowIndices.length - 1];
    const isDraggingUp = firstTargetIndex < dragFillState.sourceRowIndex;
    if (isDraggingUp) {
      const firstTargetKey = getCellRefKey(firstTargetIndex, dragFillState.sourceColumn);
      const firstTargetCell = cellRefs.current.get(firstTargetKey);
      if (firstTargetCell) {
        const firstTargetRect = firstTargetCell.getBoundingClientRect();
        setDragOverlay({
          top: firstTargetRect.top - tableRect.top,
          left: sourceRect.left - tableRect.left,
          width: sourceRect.width,
          height: sourceRect.bottom - firstTargetRect.top,
        });
      }
    } else {
      const lastTargetKey = getCellRefKey(lastTargetIndex, dragFillState.sourceColumn);
      const lastTargetCell = cellRefs.current.get(lastTargetKey);
      if (lastTargetCell) {
        const lastTargetRect = lastTargetCell.getBoundingClientRect();
        setDragOverlay({
          top: sourceRect.top - tableRect.top,
          left: sourceRect.left - tableRect.left,
          width: sourceRect.width,
          height: lastTargetRect.bottom - sourceRect.top,
        });
      }
    }
  }, [dragFillState]);

  const handleCellClick = useCallback(
    (rowIndex: number, column: FillableColumn, e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedCell({ rowIndex, column });
    },
    []
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tableRef.current && !tableRef.current.contains(e.target as Node)) {
        setSelectedCell(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFillHandleMouseDown = useCallback(
    (rowIndex: number, column: FillableColumn, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isDraggingRef.current = true;
      mouseYRef.current = e.clientY;
      dragDirectionRef.current = null;
      setDragFillState({
        isActive: true,
        sourceRowIndex: rowIndex,
        sourceColumn: column,
        targetRowIndices: [],
        mouseY: e.clientY,
      });
    },
    []
  );

  const handleCellMouseEnter = useCallback(
    (rowIndex: number, column: FillableColumn) => {
      if (!dragFillState.isActive || column !== dragFillState.sourceColumn) return;
      if (rowIndex === dragFillState.sourceRowIndex) {
        setDragFillState(prev => ({ ...prev, targetRowIndices: [] }));
        return;
      }
      const indices: number[] = [];
      if (rowIndex > dragFillState.sourceRowIndex) {
        for (let i = dragFillState.sourceRowIndex + 1; i <= rowIndex; i++) indices.push(i);
      } else {
        for (let i = rowIndex; i < dragFillState.sourceRowIndex; i++) indices.push(i);
      }
      setDragFillState(prev => ({ ...prev, targetRowIndices: indices }));
    },
    [dragFillState.isActive, dragFillState.sourceColumn, dragFillState.sourceRowIndex]
  );

  const applyDragFill = useCallback(() => {
    if (!dragFillState.isActive || dragFillState.targetRowIndices.length === 0) {
      setDragFillState({
        isActive: false,
        sourceRowIndex: -1,
        sourceColumn: 'taxonomyType',
        targetRowIndices: [],
        mouseY: undefined,
      });
      return;
    }
    const sourceThreshold = brandConfig.thresholds[dragFillState.sourceRowIndex];
    if (!sourceThreshold) {
      setDragFillState({
        isActive: false,
        sourceRowIndex: -1,
        sourceColumn: 'taxonomyType',
        targetRowIndices: [],
        mouseY: undefined,
      });
      return;
    }
    let updatedRows = 0;
    dragFillState.targetRowIndices.forEach(targetIndex => {
      const targetThreshold = brandConfig.thresholds[targetIndex];
      switch (dragFillState.sourceColumn) {
        case 'taxonomyType':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'taxonomyType',
            sourceThreshold.taxonomyType || ''
          );
          onUpdateThreshold(brandConfig.brandId, targetIndex, 'metric', 'taxonomy_score');
          updatedRows++;
          break;
        case 'valueType':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'taxonomyValueType',
            sourceThreshold.taxonomyValueType || ''
          );
          if (sourceThreshold.taxonomyValueType === 'updated_at') {
            onUpdateThreshold(
              brandConfig.brandId,
              targetIndex,
              'threshold',
              sourceThreshold.threshold || new Date().toISOString()
            );
          }
          updatedRows++;
          break;
        case 'operator':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'operator',
            sourceThreshold.operator || '<'
          );
          updatedRows++;
          break;
        case 'threshold':
          onUpdateThreshold(
            brandConfig.brandId,
            targetIndex,
            'threshold',
            sourceThreshold.threshold || 0
          );
          if (targetThreshold) {
            const errorKey = getThresholdErrorKey(brandConfig.brandId, targetThreshold.id);
            setLocalInputValues(prev => ({
              ...prev,
              [errorKey]: String(sourceThreshold.threshold || 0),
            }));
          }
          updatedRows++;
          break;
      }
    });
    if (updatedRows > 0) {
      Toastify('success', `Filled ${updatedRows} row(s)`);
    }
    setDragFillState({
      isActive: false,
      sourceRowIndex: -1,
      sourceColumn: 'taxonomyType',
      targetRowIndices: [],
      mouseY: undefined,
    });
    setSelectedCell(null);
  }, [dragFillState, brandConfig.thresholds, brandConfig.brandId, onUpdateThreshold]);

  const calculateTargetRowsFromMouseY = useCallback(
    (mouseY: number): number[] => {
      if (!tableRef.current || !dragFillState.isActive) return [];
      const tableRect = tableRef.current.getBoundingClientRect();
      const relativeY = mouseY - tableRect.top;
      let targetRowIndex = -1;
      for (let i = 0; i < brandConfig.thresholds.length; i++) {
        const cellKey = getCellRefKey(i, dragFillState.sourceColumn);
        const cell = cellRefs.current.get(cellKey);
        if (cell) {
          const cellRect = cell.getBoundingClientRect();
          const cellRelativeTop = cellRect.top - tableRect.top;
          const cellRelativeBottom = cellRect.bottom - tableRect.top;
          if (relativeY >= cellRelativeTop && relativeY <= cellRelativeBottom) {
            targetRowIndex = i;
            break;
          }
          if (relativeY < cellRelativeTop && i > 0) {
            targetRowIndex = i - 1;
            break;
          }
        }
      }
      if (targetRowIndex === -1 && brandConfig.thresholds.length > 0) {
        const lastIndex = brandConfig.thresholds.length - 1;
        const lastCellKey = getCellRefKey(lastIndex, dragFillState.sourceColumn);
        const lastCell = cellRefs.current.get(lastCellKey);
        if (lastCell && mouseY > lastCell.getBoundingClientRect().bottom) {
          targetRowIndex = lastIndex;
        }
      }
      if (targetRowIndex === -1 || targetRowIndex === dragFillState.sourceRowIndex) return [];
      const indices: number[] = [];
      if (targetRowIndex > dragFillState.sourceRowIndex) {
        for (let i = dragFillState.sourceRowIndex + 1; i <= targetRowIndex; i++) indices.push(i);
      } else {
        for (let i = targetRowIndex; i < dragFillState.sourceRowIndex; i++) indices.push(i);
      }
      return indices;
    },
    [
      dragFillState.isActive,
      dragFillState.sourceRowIndex,
      dragFillState.sourceColumn,
      brandConfig.thresholds.length,
    ]
  );

  useEffect(() => {
    if (!dragFillState.isActive) {
      isDraggingRef.current = false;
      mouseYRef.current = null;
      stopAutoScroll();
      return;
    }
    let rafId: number | null = null;
    let hasMovedMouse = false;
    const initialMouseY = dragFillState.mouseY;
    const updateOverlayFromMousePosition = () => {
      if (!dragFillState.isActive || mouseYRef.current === null) return;
      const targetIndices = calculateTargetRowsFromMouseY(mouseYRef.current);
      setDragFillState(prev => {
        if (
          prev.targetRowIndices.length !== targetIndices.length ||
          prev.targetRowIndices.some((idx, i) => idx !== targetIndices[i])
        ) {
          return { ...prev, targetRowIndices: targetIndices };
        }
        return prev;
      });
      rafId = window.requestAnimationFrame(updateOverlayFromMousePosition);
    };
    const handleMouseMove = (e: MouseEvent) => {
      mouseYRef.current = e.clientY;
      if (initialMouseY !== undefined) {
        if (e.clientY > initialMouseY + 5) dragDirectionRef.current = 'down';
        else if (e.clientY < initialMouseY - 5) dragDirectionRef.current = 'up';
      }
      if (!hasMovedMouse && initialMouseY !== undefined) {
        if (Math.abs(e.clientY - initialMouseY) > 10) {
          hasMovedMouse = true;
          startAutoScroll();
        }
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    rafId = window.requestAnimationFrame(updateOverlayFromMousePosition);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      isDraggingRef.current = false;
      mouseYRef.current = null;
      dragDirectionRef.current = null;
      stopAutoScroll();
    };
  }, [
    dragFillState.isActive,
    dragFillState.mouseY,
    startAutoScroll,
    stopAutoScroll,
    calculateTargetRowsFromMouseY,
  ]);

  useEffect(() => {
    const handleMouseUp = () => {
      if (dragFillState.isActive) {
        isDraggingRef.current = false;
        mouseYRef.current = null;
        dragDirectionRef.current = null;
        stopAutoScroll();
        applyDragFill();
      }
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [dragFillState.isActive, applyDragFill, stopAutoScroll]);

  const isCellInFillRange = (rowIndex: number, column: FillableColumn) => {
    return (
      dragFillState.isActive &&
      dragFillState.sourceColumn === column &&
      dragFillState.targetRowIndices.includes(rowIndex)
    );
  };

  useEffect(() => {
    return () => {
      stopAutoScroll();
      isDraggingRef.current = false;
      mouseYRef.current = null;
    };
  }, [stopAutoScroll]);

  // State to track validation errors for thresholds
  const [, setThresholdErrors] = useState<Record<string, string>>({});

  // State to track local input values (allows empty string while typing)
  const [localInputValues, setLocalInputValues] = useState<Record<string, string>>({});

  // Track initialized input keys to avoid overwriting user input
  const initializedKeysRef = useRef<Set<string>>(new Set());

  // State for selected taxonomy types and value types in filter dropdowns
  const [selectedTaxonomyTypes, setSelectedTaxonomyTypes] = useState<string[]>([]);
  const [selectedValueTypes, setSelectedValueTypes] = useState<string[]>([]);

  // Track which taxonomy type-value type combinations have been auto-created
  const autoCreatedCombinationsRef = useRef<Set<string>>(new Set());
  // Track which threshold IDs were auto-created for which combinations
  const autoCreatedThresholdsRef = useRef<Map<string, string[]>>(new Map());
  // Track previous selected types to detect removals
  const prevSelectedTaxonomyTypesRef = useRef<string[]>(selectedTaxonomyTypes);
  const prevSelectedValueTypesRef = useRef<string[]>(selectedValueTypes);
  // Track pending combination creations that need to be populated after rows are added
  const pendingCombinationCreationsRef = useRef<Array<{ taxonomyType: string; valueType: string }>>(
    []
  );
  // Track threshold count before creating new rows
  const thresholdCountBeforeCreationRef = useRef<number>(0);
  // Track previous thresholds to detect changes without causing loops
  const prevThresholdsRef = useRef<BrandThreshold[]>(brandConfig.thresholds);
  // Track if dropdowns have been initialized from existing data
  const dropdownsInitializedRef = useRef<boolean>(false);

  // Helper function to generate combination key
  const getCombinationKey = (taxonomyType: string, valueType: string) =>
    `${taxonomyType}:${valueType}`;

  // Get config for a specific metric type from alert type
  const getConfigForType = useMemo(() => {
    return (
      taxonomyValueType: string | undefined
    ): { value?: number; max_value?: number } | undefined => {
      const alertType = alertTypes?.find(at => at.display_name === toolName);
      if (!alertType?.default_threshold_config) return undefined;

      // Find the config that matches the metric type (percentage or count)
      const matchingConfig = alertType.default_threshold_config.find(
        config => 'metric' in config && config.metric === taxonomyValueType
      );

      if (matchingConfig && 'value' in matchingConfig) {
        return {
          value:
            matchingConfig.value !== undefined
              ? parseFloat(String(matchingConfig.value))
              : undefined,
          max_value:
            'max_value' in matchingConfig && matchingConfig.max_value !== undefined
              ? parseFloat(String(matchingConfig.max_value))
              : undefined,
        };
      }

      // Fallback to first config if no match found
      const firstConfig = alertType.default_threshold_config[0];
      if (firstConfig && 'value' in firstConfig) {
        return {
          value:
            firstConfig.value !== undefined ? parseFloat(String(firstConfig.value)) : undefined,
          max_value:
            'max_value' in firstConfig && firstConfig.max_value !== undefined
              ? parseFloat(String(firstConfig.max_value))
              : undefined,
        };
      }

      return undefined;
    };
  }, [alertTypes, toolName]);

  // Get max_value from alert type for validation based on taxonomyValueType
  const getMaxValueForType = useMemo(() => {
    return (taxonomyValueType: string | undefined): number | undefined => {
      const config = getConfigForType(taxonomyValueType);
      return config?.max_value;
    };
  }, [getConfigForType]);

  // Get default threshold value from alert type based on taxonomyValueType
  const getDefaultValueForType = useMemo(() => {
    return (taxonomyValueType: string | undefined): number | undefined => {
      const config = getConfigForType(taxonomyValueType);
      return config?.value;
    };
  }, [getConfigForType]);

  // Validate all thresholds and update errors state (for display purposes)
  useEffect(() => {
    const errors: Record<string, string> = {};
    brandConfig.thresholds.forEach(threshold => {
      const errorKey = getThresholdErrorKey(brandConfig.brandId, threshold.id);
      // Skip max value validation for 'count' type
      const maxValue =
        threshold.taxonomyValueType === 'count'
          ? undefined
          : getMaxValueForType(threshold.taxonomyValueType);
      const error = validateThreshold(threshold.threshold, maxValue);
      if (error) {
        errors[errorKey] = error;
      }
    });

    // Update errors state only if changed
    setThresholdErrors(prev => {
      const hasChanges =
        Object.keys(errors).length !== Object.keys(prev).length ||
        Object.keys(errors).some(key => errors[key] !== prev[key]);
      return hasChanges ? errors : prev;
    });
  }, [brandConfig.thresholds, brandConfig.brandId, getMaxValueForType]);

  // Sync local input values when thresholds change externally (only for new thresholds)
  useEffect(() => {
    const updatedValues: Record<string, string> = {};
    brandConfig.thresholds.forEach(threshold => {
      const errorKey = getThresholdErrorKey(brandConfig.brandId, threshold.id);
      // Only initialize if not already initialized (to preserve user's typing)
      if (!initializedKeysRef.current.has(errorKey)) {
        if (threshold.threshold !== null && threshold.threshold !== undefined) {
          updatedValues[errorKey] = String(threshold.threshold);
          initializedKeysRef.current.add(errorKey);
        }
      }
    });
    if (Object.keys(updatedValues).length > 0) {
      setLocalInputValues(prev => ({ ...prev, ...updatedValues }));
    }
  }, [brandConfig.thresholds, brandConfig.brandId]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = brandConfig.thresholds.findIndex(t => t.id === active.id);
      const newIndex = brandConfig.thresholds.findIndex(t => t.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorderThresholds(brandConfig.brandId, oldIndex, newIndex);
      }
    }
  };

  // Auto-row creation logic for taxonomy type-value type combinations
  // Trigger when selectedTaxonomyTypes or selectedValueTypes change
  useEffect(() => {
    const prevTypes = prevSelectedTaxonomyTypesRef.current;
    const prevValues = prevSelectedValueTypesRef.current;
    const currentTypes = selectedTaxonomyTypes;
    const currentValues = selectedValueTypes;

    // Only proceed if types or values actually changed
    const typesChanged =
      prevTypes.length !== currentTypes.length || prevTypes.some((t, i) => t !== currentTypes[i]);
    const valuesChanged =
      prevValues.length !== currentValues.length ||
      prevValues.some((v, i) => v !== currentValues[i]);

    if (!typesChanged && !valuesChanged) {
      return;
    }

    // Skip auto-creation if this is the initial population from existing thresholds
    const isInitialPopulation = prevTypes.length === 0 && prevValues.length === 0;
    if (isInitialPopulation && brandConfig.thresholds.length > 0) {
      // Just update the refs and return without creating new rows
      prevSelectedTaxonomyTypesRef.current = currentTypes;
      prevSelectedValueTypesRef.current = currentValues;
      return;
    }

    // Generate all current combinations
    const currentCombinations = new Set<string>();
    currentTypes.forEach(type => {
      currentValues.forEach(value => {
        currentCombinations.add(getCombinationKey(type, value));
      });
    });

    // Generate all previous combinations
    const prevCombinations = new Set<string>();
    prevTypes.forEach(type => {
      prevValues.forEach(value => {
        prevCombinations.add(getCombinationKey(type, value));
      });
    });

    // Find removed combinations
    const removedCombinations = Array.from(prevCombinations).filter(
      combo => !currentCombinations.has(combo)
    );

    // Remove auto-created rows for removed combinations
    if (removedCombinations.length > 0) {
      const thresholdIdsToRemove = new Set<string>();

      removedCombinations.forEach(comboKey => {
        const trackedIds = autoCreatedThresholdsRef.current.get(comboKey) || [];
        trackedIds.forEach(id => thresholdIdsToRemove.add(id));

        // Remove from tracking maps
        autoCreatedThresholdsRef.current.delete(comboKey);
        autoCreatedCombinationsRef.current.delete(comboKey);

        // Remove any other rows that match the same combination
        prevThresholdsRef.current.forEach(threshold => {
          const thresholdCombo = getCombinationKey(
            threshold.taxonomyType || '',
            threshold.taxonomyValueType || ''
          );
          if (thresholdCombo === comboKey) {
            thresholdIdsToRemove.add(threshold.id);
          }
        });
      });

      if (thresholdIdsToRemove.size > 0) {
        // Calculate indices and remove in reverse order
        const indicesToRemove = Array.from(thresholdIdsToRemove)
          .map(thresholdId => prevThresholdsRef.current.findIndex(t => t.id === thresholdId))
          .filter(index => index !== -1)
          .sort((a, b) => b - a);

        indicesToRemove.forEach(thresholdIndex => {
          onRemoveThresholdRow(brandConfig.brandId, thresholdIndex);
        });
      }
    }

    // Update refs for next comparison
    prevSelectedTaxonomyTypesRef.current = currentTypes;
    prevSelectedValueTypesRef.current = currentValues;

    // Create rows for all taxonomy type-value type combinations
    if (currentTypes.length > 0 && currentValues.length > 0) {
      const combinationsToCreate: Array<{ taxonomyType: string; valueType: string }> = [];

      currentTypes.forEach(taxonomyType => {
        currentValues.forEach(valueType => {
          const comboKey = getCombinationKey(taxonomyType, valueType);

          // Skip if already auto-created
          if (autoCreatedCombinationsRef.current.has(comboKey)) {
            return;
          }

          // Check if a row with this combination already exists
          const combinationExists = prevThresholdsRef.current.some(
            t => t.taxonomyType === taxonomyType && t.taxonomyValueType === valueType
          );

          if (!combinationExists) {
            combinationsToCreate.push({ taxonomyType, valueType });
          } else {
            // Mark as auto-created even if it already exists
            autoCreatedCombinationsRef.current.add(comboKey);
            // Track existing threshold
            const existingThreshold = prevThresholdsRef.current.find(
              t => t.taxonomyType === taxonomyType && t.taxonomyValueType === valueType
            );
            if (existingThreshold?.id) {
              const existingIds = autoCreatedThresholdsRef.current.get(comboKey) || [];
              if (!existingIds.includes(existingThreshold.id)) {
                autoCreatedThresholdsRef.current.set(comboKey, [
                  ...existingIds,
                  existingThreshold.id,
                ]);
              }
            }
          }
        });
      });

      // Create rows for new combinations
      if (combinationsToCreate.length > 0) {
        // Find existing empty rows first (rows without taxonomyType and taxonomyValueType)
        const emptyRows = prevThresholdsRef.current
          .map((threshold, index) => ({ threshold, index }))
          .filter(({ threshold }) => !threshold.taxonomyType && !threshold.taxonomyValueType);

        // Fill existing empty rows
        const combinationsToFillExisting = combinationsToCreate.slice(0, emptyRows.length);
        const combinationsToCreateNew = combinationsToCreate.slice(emptyRows.length);

        combinationsToFillExisting.forEach((combo, idx) => {
          if (idx < emptyRows.length) {
            const thresholdIndex = emptyRows[idx].index;
            const threshold = prevThresholdsRef.current[thresholdIndex];
            const comboKey = getCombinationKey(combo.taxonomyType, combo.valueType);

            // Update taxonomy type and value type fields
            onUpdateThreshold(
              brandConfig.brandId,
              thresholdIndex,
              'taxonomyType',
              combo.taxonomyType as 'ad' | 'adset' | 'campaign'
            );
            onUpdateThreshold(
              brandConfig.brandId,
              thresholdIndex,
              'taxonomyValueType',
              combo.valueType as 'percentage' | 'count' | 'updated_at'
            );
            // Set metric to taxonomy_score
            onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'metric', 'taxonomy_score');

            // Set default threshold value from API config
            if (combo.valueType === 'percentage' || combo.valueType === 'count') {
              const defaultValue = getDefaultValueForType(combo.valueType);
              if (defaultValue !== undefined) {
                onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'threshold', defaultValue);
                // Also update local input value
                if (threshold?.id) {
                  const rowErrorKey = getThresholdErrorKey(brandConfig.brandId, threshold.id);
                  setLocalInputValues(prev => ({
                    ...prev,
                    [rowErrorKey]: String(defaultValue),
                  }));
                }
              }
            } else if (combo.valueType === 'updated_at') {
              onUpdateThreshold(
                brandConfig.brandId,
                thresholdIndex,
                'threshold',
                new Date().toISOString()
              );
            }

            // Track this threshold as auto-created
            if (threshold?.id) {
              const existingIds = autoCreatedThresholdsRef.current.get(comboKey) || [];
              if (!existingIds.includes(threshold.id)) {
                autoCreatedThresholdsRef.current.set(comboKey, [...existingIds, threshold.id]);
              }
            }
            autoCreatedCombinationsRef.current.add(comboKey);
          }
        });

        // Create new rows for remaining combinations
        thresholdCountBeforeCreationRef.current = prevThresholdsRef.current.length;
        pendingCombinationCreationsRef.current = combinationsToCreateNew;

        combinationsToCreateNew.forEach(() => {
          onAddThresholdRow(brandConfig.brandId);
        });
      }
    } else {
      // Clear tracking when all types or values are deselected
      const allAutoCreatedIds = Array.from(autoCreatedThresholdsRef.current.values()).flat();
      const indicesToRemove = allAutoCreatedIds
        .map(thresholdId => prevThresholdsRef.current.findIndex(t => t.id === thresholdId))
        .filter(index => index !== -1)
        .sort((a, b) => b - a);

      indicesToRemove.forEach(thresholdIndex => {
        onRemoveThresholdRow(brandConfig.brandId, thresholdIndex);
      });

      autoCreatedThresholdsRef.current.clear();
      autoCreatedCombinationsRef.current.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaxonomyTypes, selectedValueTypes, brandConfig.brandId]);

  // Update prevThresholdsRef when thresholds change (but don't trigger auto-creation)
  useEffect(() => {
    prevThresholdsRef.current = brandConfig.thresholds;
  }, [brandConfig.thresholds]);

  // Process pending combination creations after new rows are added
  useEffect(() => {
    const pending = pendingCombinationCreationsRef.current;
    const thresholdCountBefore = thresholdCountBeforeCreationRef.current;

    if (pending.length > 0 && brandConfig.thresholds.length > thresholdCountBefore) {
      // Find newly created empty thresholds
      const newlyCreatedEmptyThresholds = brandConfig.thresholds
        .map((threshold, index) => ({ threshold, index }))
        .filter(
          ({ threshold, index }) =>
            index >= thresholdCountBefore && !threshold.taxonomyType && !threshold.taxonomyValueType
        );

      if (newlyCreatedEmptyThresholds.length >= pending.length) {
        // Clear pending creations immediately
        pendingCombinationCreationsRef.current = [];
        thresholdCountBeforeCreationRef.current = 0;

        // Populate the newly created rows with taxonomy type and value type combinations
        pending.forEach((combo, idx) => {
          if (idx < newlyCreatedEmptyThresholds.length) {
            const thresholdIndex = newlyCreatedEmptyThresholds[idx].index;
            const comboKey = getCombinationKey(combo.taxonomyType, combo.valueType);

            // Update taxonomy type and value type fields
            onUpdateThreshold(
              brandConfig.brandId,
              thresholdIndex,
              'taxonomyType',
              combo.taxonomyType as 'ad' | 'adset' | 'campaign'
            );
            onUpdateThreshold(
              brandConfig.brandId,
              thresholdIndex,
              'taxonomyValueType',
              combo.valueType as 'percentage' | 'count' | 'updated_at'
            );
            // Set metric to taxonomy_score
            onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'metric', 'taxonomy_score');

            // Set default threshold value from API config
            if (combo.valueType === 'percentage' || combo.valueType === 'count') {
              const defaultValue = getDefaultValueForType(combo.valueType);
              if (defaultValue !== undefined) {
                onUpdateThreshold(brandConfig.brandId, thresholdIndex, 'threshold', defaultValue);
                // Also update local input value
                const thresholdAtIndex = brandConfig.thresholds[thresholdIndex];
                if (thresholdAtIndex?.id) {
                  const rowErrorKey = getThresholdErrorKey(
                    brandConfig.brandId,
                    thresholdAtIndex.id
                  );
                  setLocalInputValues(prev => ({
                    ...prev,
                    [rowErrorKey]: String(defaultValue),
                  }));
                }
              }
            } else if (combo.valueType === 'updated_at') {
              onUpdateThreshold(
                brandConfig.brandId,
                thresholdIndex,
                'threshold',
                new Date().toISOString()
              );
            }

            const threshold = brandConfig.thresholds[thresholdIndex];
            if (threshold?.id) {
              const existingIds = autoCreatedThresholdsRef.current.get(comboKey) || [];
              if (!existingIds.includes(threshold.id)) {
                autoCreatedThresholdsRef.current.set(comboKey, [...existingIds, threshold.id]);
              }
            }
            autoCreatedCombinationsRef.current.add(comboKey);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandConfig.thresholds]);

  // Ensure there's always at least one empty row
  useEffect(() => {
    // Check if there are any rows at all
    if (brandConfig.thresholds.length === 0) {
      onAddThresholdRow(brandConfig.brandId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandConfig.thresholds.length]);

  // Initialize dropdowns from existing thresholds (only on first load)
  useEffect(() => {
    if (!dropdownsInitializedRef.current && brandConfig.thresholds.length > 0) {
      const currentTaxonomyTypes = new Set<string>();
      const currentValueTypes = new Set<string>();

      brandConfig.thresholds.forEach(threshold => {
        if (threshold.taxonomyType) {
          currentTaxonomyTypes.add(threshold.taxonomyType);
        }
        if (threshold.taxonomyValueType) {
          currentValueTypes.add(threshold.taxonomyValueType);
        }
      });

      if (currentTaxonomyTypes.size > 0 || currentValueTypes.size > 0) {
        setSelectedTaxonomyTypes(Array.from(currentTaxonomyTypes));
        setSelectedValueTypes(Array.from(currentValueTypes));
        dropdownsInitializedRef.current = true;
      }
    }
  }, [brandConfig.thresholds, brandConfig.brandId]);

  // Taxonomy type options
  const taxonomyTypeOptions = [
    { label: 'Campaign', value: 'campaign' },
    { label: 'Adset', value: 'adset' },
    { label: 'Ad', value: 'ad' },
  ];

  // Taxonomy value type options
  const taxonomyValueTypeOptions = [
    { label: 'Percentage', value: 'percentage' },
    { label: 'Count', value: 'count' },
    { label: 'Updated At', value: 'updated_at' },
  ];

  // Handle row removal with dropdown update
  const handleRemoveRow = (thresholdIndex: number) => {
    const thresholdToRemove = brandConfig.thresholds[thresholdIndex];
    const taxonomyTypeToCheck = thresholdToRemove.taxonomyType;
    const valueTypeToCheck = thresholdToRemove.taxonomyValueType;
    const comboKey = getCombinationKey(taxonomyTypeToCheck || '', valueTypeToCheck || '');

    // Remove from auto-created tracking
    if (comboKey && autoCreatedCombinationsRef.current.has(comboKey)) {
      const trackedIds = autoCreatedThresholdsRef.current.get(comboKey) || [];
      const updatedIds = trackedIds.filter(id => id !== thresholdToRemove.id);
      if (updatedIds.length === 0) {
        autoCreatedThresholdsRef.current.delete(comboKey);
        autoCreatedCombinationsRef.current.delete(comboKey);
      } else {
        autoCreatedThresholdsRef.current.set(comboKey, updatedIds);
      }
    }

    // Remove the row
    onRemoveThresholdRow(brandConfig.brandId, thresholdIndex);

    // Check if the taxonomy type is still used in other rows (excluding the one being removed)
    if (taxonomyTypeToCheck) {
      const isTaxonomyTypeStillUsed = brandConfig.thresholds.some(
        (t, idx) => idx !== thresholdIndex && t.taxonomyType === taxonomyTypeToCheck
      );
      if (!isTaxonomyTypeStillUsed) {
        setSelectedTaxonomyTypes(prev => prev.filter(t => t !== taxonomyTypeToCheck));
      }
    }

    // Check if the value type is still used in other rows (excluding the one being removed)
    if (valueTypeToCheck) {
      const isValueTypeStillUsed = brandConfig.thresholds.some(
        (t, idx) => idx !== thresholdIndex && t.taxonomyValueType === valueTypeToCheck
      );
      if (!isValueTypeStillUsed) {
        setSelectedValueTypes(prev => prev.filter(v => v !== valueTypeToCheck));
      }
    }
  };

  // Handle clear all with dropdown reset
  const handleClearAll = () => {
    // Clear all tracking
    autoCreatedCombinationsRef.current.clear();
    autoCreatedThresholdsRef.current.clear();
    dropdownsInitializedRef.current = false;

    // Clear thresholds and dropdowns
    onClearAllThresholds(brandConfig.brandId);
    setSelectedTaxonomyTypes([]);
    setSelectedValueTypes([]);
  };

  return (
    <div className="mb-6 w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Brand-Specific Thresholds ({brandConfig.brandName})
        </h3>
        {brandConfig.thresholds.length > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClearAll}
            disabled={isDisabled}
            className="text-xs"
          >
            Clear All
          </Button>
        )}
      </div>

      <div
        ref={scrollContainerRef}
        className="overflow-x-auto w-full relative"
        style={{ maxHeight: '600px', overflowY: 'auto' }}
      >
        {/* Drag Fill Selection Overlay */}
        {dragOverlay && dragFillState.isActive && (
          <div
            className="absolute pointer-events-none z-20 border-2 border-blue-500 bg-blue-500/10 rounded-sm"
            style={{
              top: dragOverlay.top,
              left: dragOverlay.left,
              width: dragOverlay.width,
              height: dragOverlay.height,
              transition: 'height 0.05s ease-out',
            }}
          >
            {dragFillState.targetRowIndices.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500" />
            )}
            {dragFillState.targetRowIndices.length > 0 && (
              <div className="absolute -right-8 top-1/2 -translate-y-1/2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded shadow-lg">
                {dragFillState.targetRowIndices.length}
              </div>
            )}
          </div>
        )}
        <table ref={tableRef} className="border-collapse border border-gray-300 w-full">
          <colgroup>
            <col style={{ width: '40px' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-1 py-2 text-center text-sm font-medium text-gray-700">
                <div className="flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-3 w-3 text-gray-400"
                  >
                    <circle cx="9" cy="5" r="1" />
                    <circle cx="9" cy="12" r="1" />
                    <circle cx="9" cy="19" r="1" />
                    <circle cx="15" cy="5" r="1" />
                    <circle cx="15" cy="12" r="1" />
                    <circle cx="15" cy="19" r="1" />
                  </svg>
                </div>
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                <div className="flex items-center">
                  <span>Taxonomy Type</span>
                  <span className="text-red-500 ml-1">*</span>
                </div>
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                <div className="flex items-center">
                  <span>Value Type</span>
                  <span className="text-red-500 ml-1">*</span>
                </div>
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                Operator
              </th>
              <th className="border border-gray-300 px-2 py-2 text-left text-sm font-medium text-gray-700">
                Enter Threshold
              </th>
              <th className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700">
                Actions
              </th>
            </tr>
            {/* Filter Row */}
            <tr className="bg-gray-50">
              <td className="border border-gray-300 px-2 py-2"></td>
              <td className="border border-gray-300 px-2 py-2">
                <div className="w-full" onClick={e => e.stopPropagation()}>
                  <FilterDropdown
                    selectedValues={selectedTaxonomyTypes}
                    onSelectionChange={values => {
                      setSelectedTaxonomyTypes(values);
                      // Mark dropdowns as initialized when user makes manual selection
                      dropdownsInitializedRef.current = true;
                    }}
                    options={taxonomyTypeOptions}
                    placeholder="Select Taxonomy Type"
                    emptyMessage="No taxonomy types available"
                    getDisplayLabel={value => {
                      const option = taxonomyTypeOptions.find(opt => opt.value === value);
                      return option?.label || value;
                    }}
                  />
                </div>
              </td>
              <td className="border border-gray-300 px-2 py-2">
                <div className="w-full" onClick={e => e.stopPropagation()}>
                  <FilterDropdown
                    selectedValues={selectedValueTypes}
                    onSelectionChange={values => {
                      setSelectedValueTypes(values);
                      // Mark dropdowns as initialized when user makes manual selection
                      dropdownsInitializedRef.current = true;
                    }}
                    options={taxonomyValueTypeOptions}
                    placeholder="Select Value Type"
                    emptyMessage="No value types available"
                    getDisplayLabel={value => {
                      const option = taxonomyValueTypeOptions.find(opt => opt.value === value);
                      return option?.label || value;
                    }}
                  />
                </div>
              </td>
              <td className="border border-gray-300 px-2 py-2"></td>
              <td className="border border-gray-300 px-2 py-2"></td>
              <td className="border border-gray-300 px-2 py-2 text-center"></td>
            </tr>
          </thead>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            autoScroll={false}
          >
            <SortableContext
              items={brandConfig.thresholds.map(t => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {brandConfig.thresholds.map((threshold, index) => {
                  const originalIndex = index;
                  const isLastRow = index === brandConfig.thresholds.length - 1;

                  // Use utility functions for cleaner logic
                  const prevThreshold = index > 0 ? brandConfig.thresholds[index - 1] : null;
                  const connToNext = checkConnectionToNext(threshold, isLastRow);
                  const connFromPrev = checkConnectionFromPrev(prevThreshold);

                  // Get border classes from utility function
                  const { firstColBorderClass, middleColBorderClass, lastColBorderClass } =
                    getBorderClasses(connFromPrev, connToNext);

                  // Calculate if threshold/actions columns have rowSpan that would occupy connector row
                  const isCurrentRowAdd = threshold.logicalOperator === 'ADD';
                  let hasRowSpanCells = false;

                  if (isCurrentRowAdd) {
                    let addChainStart = originalIndex;
                    let addChainEnd = originalIndex;
                    let addChainLength = 1;

                    // Find the start of the ADD chain
                    while (
                      addChainStart > 0 &&
                      brandConfig.thresholds[addChainStart - 1].logicalOperator === 'ADD'
                    ) {
                      addChainStart--;
                    }

                    // Find the end of the ADD chain
                    while (
                      addChainEnd < brandConfig.thresholds.length - 1 &&
                      brandConfig.thresholds[addChainEnd].logicalOperator === 'ADD'
                    ) {
                      addChainEnd++;
                    }

                    addChainLength = addChainEnd - addChainStart + 1;

                    // For even-numbered chains, the first row of second half has rowSpan
                    // which spans into the connector row (which comes after it)
                    if (addChainLength % 2 === 0) {
                      const middleIndex = addChainStart + addChainLength / 2 - 1;
                      // If current row is the middle row (has rowSpan), it will span into connector row
                      if (originalIndex === middleIndex) {
                        hasRowSpanCells = true;
                      }
                    }
                  }

                  const logicalOperatorRow = !isLastRow && (
                    <tr>
                      <td
                        colSpan={hasRowSpanCells ? 4 : 6}
                        className={`${hasRowSpanCells ? 'border-l' : 'border-x'} border-gray-300 px-4 py-3 bg-gray-50/30`}
                        style={{ borderTop: 'none', borderBottom: 'none' }}
                      >
                        <div
                          className="flex items-center relative pl-8"
                          style={{ minHeight: '56px' }}
                        >
                          {/* Curly brace connector - only show if AND, OR, or ADD */}
                          {threshold.logicalOperator && threshold.logicalOperator !== 'NONE' && (
                            <div
                              className="absolute flex flex-col items-center"
                              style={{ height: '100%', width: '24px', left: '16px' }}
                            >
                              {/* Top curve */}
                              <div
                                className={`w-full h-1/2 border-l-2 border-b-2 rounded-bl-lg ${
                                  threshold.logicalOperator === 'AND'
                                    ? 'border-green-500'
                                    : threshold.logicalOperator === 'OR'
                                      ? 'border-orange-500'
                                      : 'border-blue-500'
                                }`}
                                style={{ borderBottomLeftRadius: '10px' }}
                              />
                              {/* Bottom curve */}
                              <div
                                className={`w-full h-1/2 border-l-2 border-t-2 rounded-tl-lg ${
                                  threshold.logicalOperator === 'AND'
                                    ? 'border-green-500'
                                    : threshold.logicalOperator === 'OR'
                                      ? 'border-orange-500'
                                      : 'border-blue-500'
                                }`}
                                style={{ borderTopLeftRadius: '10px' }}
                              />
                            </div>
                          )}

                          {/* Logical Operator Selector */}
                          <div className="flex items-center gap-3 ml-8 flex-wrap">
                            <SingleSelect
                              key={`${threshold.id}-logical-op-${threshold.logicalOperator || 'NONE'}`}
                              defaultValue={threshold.logicalOperator || 'NONE'}
                              disabled={isDisabled}
                              onChange={(value: string) => {
                                const previousOperator = threshold.logicalOperator || 'NONE';

                                if (onUpdateLogicalOperator) {
                                  onUpdateLogicalOperator(
                                    brandConfig.brandId,
                                    originalIndex,
                                    value as 'AND' | 'OR' | 'NONE' | 'ADD'
                                  );

                                  // Helper function to find all connected rows after the operator change
                                  const findConnectedRowsAfterChange = (
                                    startIndex: number,
                                    newOperator: string
                                  ): number[] => {
                                    const connectedIndices = [startIndex];
                                    const visited = new Set<number>([startIndex]);
                                    const queue = [startIndex];

                                    while (queue.length > 0) {
                                      const currentIdx = queue.shift()!;

                                      // Check previous row
                                      if (currentIdx > 0) {
                                        const prevIdx = currentIdx - 1;
                                        const prevThreshold = brandConfig.thresholds[prevIdx];
                                        // Use the new operator for the current index when checking
                                        const effectiveOperator =
                                          prevIdx === startIndex - 1
                                            ? newOperator
                                            : prevThreshold.logicalOperator;
                                        if (
                                          !visited.has(prevIdx) &&
                                          effectiveOperator &&
                                          effectiveOperator !== 'NONE'
                                        ) {
                                          visited.add(prevIdx);
                                          connectedIndices.push(prevIdx);
                                          queue.push(prevIdx);
                                        }
                                      }

                                      // Check next row
                                      const effectiveOperator =
                                        currentIdx === startIndex
                                          ? newOperator
                                          : brandConfig.thresholds[currentIdx].logicalOperator;
                                      if (
                                        currentIdx < brandConfig.thresholds.length - 1 &&
                                        effectiveOperator &&
                                        effectiveOperator !== 'NONE'
                                      ) {
                                        const nextIdx = currentIdx + 1;
                                        if (!visited.has(nextIdx)) {
                                          visited.add(nextIdx);
                                          connectedIndices.push(nextIdx);
                                          queue.push(nextIdx);
                                        }
                                      }
                                    }

                                    return connectedIndices.sort((a, b) => a - b);
                                  };

                                  // If changing from NONE to something else, sync value types only for ADD
                                  if (previousOperator === 'NONE' && value !== 'NONE') {
                                    const currentValueType = threshold.taxonomyValueType;

                                    // If changing to ADD, sync to current row's value type
                                    if (value === 'ADD' && currentValueType) {
                                      const connectedIndices = findConnectedRowsAfterChange(
                                        originalIndex,
                                        value
                                      );
                                      connectedIndices.forEach(idx => {
                                        if (
                                          brandConfig.thresholds[idx].taxonomyValueType !==
                                          currentValueType
                                        ) {
                                          onUpdateThreshold(
                                            brandConfig.brandId,
                                            idx,
                                            'taxonomyValueType',
                                            currentValueType
                                          );
                                        }
                                      });
                                    }
                                    // For AND/OR, do not sync value types
                                  }
                                  // If changing to ADD (from any state), sync taxonomyValueType across the chain
                                  else if (value === 'ADD') {
                                    const currentValueType = threshold.taxonomyValueType;

                                    if (currentValueType) {
                                      // Find all rows in the ADD chain
                                      let chainStart = originalIndex;
                                      let chainEnd = originalIndex + 1; // The row after this one

                                      // Go backwards to find the start of any existing ADD chain
                                      while (
                                        chainStart > 0 &&
                                        brandConfig.thresholds[chainStart - 1].logicalOperator ===
                                          'ADD'
                                      ) {
                                        chainStart--;
                                      }

                                      // Go forwards to find the end of the ADD chain
                                      while (
                                        chainEnd < brandConfig.thresholds.length - 1 &&
                                        brandConfig.thresholds[chainEnd].logicalOperator === 'ADD'
                                      ) {
                                        chainEnd++;
                                      }

                                      // Update taxonomyValueType to current row's value type for all rows in the chain
                                      for (let i = chainStart; i <= chainEnd; i++) {
                                        if (
                                          brandConfig.thresholds[i].taxonomyValueType !==
                                          currentValueType
                                        ) {
                                          onUpdateThreshold(
                                            brandConfig.brandId,
                                            i,
                                            'taxonomyValueType',
                                            currentValueType
                                          );
                                        }
                                      }
                                    }
                                  }
                                }
                              }}
                              options={[
                                { label: 'AND', value: 'AND' },
                                { label: 'OR', value: 'OR' },
                                { label: 'ADD', value: 'ADD' },
                                { label: 'NONE', value: 'NONE' },
                              ]}
                              placeholder="Select Operator"
                              containerClassName="w-36"
                            />

                            {/* Visual indicator badge */}
                            {threshold.logicalOperator === 'AND' && (
                              <span className="px-3 py-1.5 text-xs font-semibold bg-green-100 text-green-700 rounded-md shadow-sm">
                                Both threshold conditions must meet
                              </span>
                            )}
                            {threshold.logicalOperator === 'OR' && (
                              <span className="px-3 py-1.5 text-xs font-semibold bg-orange-100 text-orange-700 rounded-md shadow-sm">
                                Any threshold condition meets
                              </span>
                            )}
                            {threshold.logicalOperator === 'ADD' && (
                              <span className="px-3 py-1.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-md shadow-sm">
                                Add threshold conditions
                              </span>
                            )}
                            {threshold.logicalOperator === 'NONE' && (
                              <span className="px-3 py-1.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-md shadow-sm">
                                No connection
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );

                  return (
                    <SortableThresholdRow
                      key={threshold.id}
                      threshold={threshold}
                      index={index}
                      originalIndex={originalIndex}
                      isLastRow={isLastRow}
                      hasConnectionToNext={connToNext}
                      hasConnectionFromPrev={connFromPrev}
                      logicalOperatorRow={logicalOperatorRow}
                    >
                      <td
                        ref={el => registerCellRef(originalIndex, 'taxonomyType', el)}
                        className={`${firstColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'taxonomyType'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'taxonomyType') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        onClick={e => handleCellClick(originalIndex, 'taxonomyType', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'taxonomyType')}
                      >
                        <div className="w-full overflow-hidden">
                          <SingleSelect
                            key={`${threshold.id}-taxonomy-type-${threshold.taxonomyType || 'empty'}`}
                            defaultValue={threshold.taxonomyType || ''}
                            disabled={isDisabled}
                            onChange={(value: string) => {
                              const oldComboKey = getCombinationKey(
                                threshold.taxonomyType || '',
                                threshold.taxonomyValueType || ''
                              );
                              const newComboKey = getCombinationKey(
                                value,
                                threshold.taxonomyValueType || ''
                              );

                              if (!value || value.trim() === '') {
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'taxonomyType',
                                  ''
                                );
                                if (
                                  oldComboKey &&
                                  autoCreatedCombinationsRef.current.has(oldComboKey)
                                ) {
                                  const trackedIds =
                                    autoCreatedThresholdsRef.current.get(oldComboKey) || [];
                                  const updatedIds = trackedIds.filter(id => id !== threshold.id);
                                  if (updatedIds.length === 0) {
                                    autoCreatedThresholdsRef.current.delete(oldComboKey);
                                    autoCreatedCombinationsRef.current.delete(oldComboKey);
                                  } else {
                                    autoCreatedThresholdsRef.current.set(oldComboKey, updatedIds);
                                  }
                                }
                              } else {
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'taxonomyType',
                                  value as 'ad' | 'adset' | 'campaign'
                                );
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'metric',
                                  'taxonomy_score'
                                );

                                if (
                                  selectedTaxonomyTypes.includes(value) &&
                                  threshold.taxonomyValueType &&
                                  selectedValueTypes.includes(threshold.taxonomyValueType) &&
                                  threshold.id
                                ) {
                                  const existingIds =
                                    autoCreatedThresholdsRef.current.get(newComboKey) || [];
                                  if (!existingIds.includes(threshold.id)) {
                                    autoCreatedThresholdsRef.current.set(newComboKey, [
                                      ...existingIds,
                                      threshold.id,
                                    ]);
                                  }
                                  autoCreatedCombinationsRef.current.add(newComboKey);
                                  if (oldComboKey && oldComboKey !== newComboKey) {
                                    const oldIds =
                                      autoCreatedThresholdsRef.current.get(oldComboKey) || [];
                                    const updatedOldIds = oldIds.filter(id => id !== threshold.id);
                                    if (updatedOldIds.length === 0) {
                                      autoCreatedThresholdsRef.current.delete(oldComboKey);
                                      autoCreatedCombinationsRef.current.delete(oldComboKey);
                                    } else {
                                      autoCreatedThresholdsRef.current.set(
                                        oldComboKey,
                                        updatedOldIds
                                      );
                                    }
                                  }
                                }
                              }
                            }}
                            options={taxonomyTypeOptions}
                            placeholder="Select Taxonomy Type"
                            containerClassName="w-full"
                            maxWidth={270}
                          />
                        </div>
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'taxonomyType' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{ transform: 'translate(50%, 50%)', borderRadius: '2px' }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'taxonomyType', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>
                      <td
                        ref={el => registerCellRef(originalIndex, 'valueType', el)}
                        className={`${middleColBorderClass} px-2 py-2 relative cursor-pointer transition-all duration-150 ${
                          selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'valueType'
                            ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                            : ''
                        } ${isCellInFillRange(originalIndex, 'valueType') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                        onClick={e => handleCellClick(originalIndex, 'valueType', e)}
                        onMouseEnter={() => handleCellMouseEnter(originalIndex, 'valueType')}
                      >
                        <div className="w-full overflow-hidden">
                          <SingleSelect
                            key={`${threshold.id}-taxonomy-value-type-${threshold.taxonomyValueType || 'empty'}`}
                            defaultValue={threshold.taxonomyValueType || ''}
                            disabled={isDisabled}
                            onChange={(value: string) => {
                              const oldComboKey = getCombinationKey(
                                threshold.taxonomyType || '',
                                threshold.taxonomyValueType || ''
                              );
                              const newComboKey = getCombinationKey(
                                threshold.taxonomyType || '',
                                value
                              );

                              // Helper function to find rows connected ONLY by ADD operators
                              const findAddConnectedRows = (startIndex: number): number[] => {
                                const connectedIndices = [startIndex];
                                const visited = new Set<number>([startIndex]);
                                const queue = [startIndex];

                                while (queue.length > 0) {
                                  const currentIdx = queue.shift()!;

                                  // Check previous row (if current row is connected from previous by ADD)
                                  if (currentIdx > 0) {
                                    const prevIdx = currentIdx - 1;
                                    const prevThreshold = brandConfig.thresholds[prevIdx];
                                    if (
                                      !visited.has(prevIdx) &&
                                      prevThreshold.logicalOperator === 'ADD'
                                    ) {
                                      visited.add(prevIdx);
                                      connectedIndices.push(prevIdx);
                                      queue.push(prevIdx);
                                    }
                                  }

                                  // Check next row (if current row has ADD connection to next)
                                  const currentThreshold = brandConfig.thresholds[currentIdx];
                                  if (
                                    currentIdx < brandConfig.thresholds.length - 1 &&
                                    currentThreshold.logicalOperator === 'ADD'
                                  ) {
                                    const nextIdx = currentIdx + 1;
                                    if (!visited.has(nextIdx)) {
                                      visited.add(nextIdx);
                                      connectedIndices.push(nextIdx);
                                      queue.push(nextIdx);
                                    }
                                  }
                                }

                                return connectedIndices.sort((a, b) => a - b);
                              };

                              if (!value || value.trim() === '') {
                                // Only update current row to empty value type (no sync for clearing)
                                onUpdateThreshold(
                                  brandConfig.brandId,
                                  originalIndex,
                                  'taxonomyValueType',
                                  ''
                                );

                                // Remove from auto-created tracking if it was auto-created
                                if (
                                  oldComboKey &&
                                  autoCreatedCombinationsRef.current.has(oldComboKey)
                                ) {
                                  const trackedIds =
                                    autoCreatedThresholdsRef.current.get(oldComboKey) || [];
                                  const updatedIds = trackedIds.filter(id => id !== threshold.id);
                                  if (updatedIds.length === 0) {
                                    autoCreatedThresholdsRef.current.delete(oldComboKey);
                                    autoCreatedCombinationsRef.current.delete(oldComboKey);
                                  } else {
                                    autoCreatedThresholdsRef.current.set(oldComboKey, updatedIds);
                                  }
                                }
                              } else {
                                // Find rows connected by ADD operator only
                                const addConnectedIndices = findAddConnectedRows(originalIndex);

                                // Update taxonomy value type for only ADD-connected rows
                                addConnectedIndices.forEach(idx => {
                                  const t = brandConfig.thresholds[idx];
                                  onUpdateThreshold(
                                    brandConfig.brandId,
                                    idx,
                                    'taxonomyValueType',
                                    value as 'percentage' | 'count' | 'updated_at'
                                  );

                                  // If changing to "updated_at", initialize threshold to current date if needed
                                  const isChangingToUpdatedAt = value === 'updated_at';
                                  const needsDateInitialization =
                                    isChangingToUpdatedAt &&
                                    (!t.threshold ||
                                      typeof t.threshold !== 'string' ||
                                      isNaN(new Date(t.threshold).getTime()) ||
                                      new Date(t.threshold).getFullYear() <= 1970);

                                  if (needsDateInitialization) {
                                    const currentDate = new Date();
                                    onUpdateThreshold(
                                      brandConfig.brandId,
                                      idx,
                                      'threshold',
                                      currentDate.toISOString()
                                    );
                                  }

                                  // For percentage and count types, set default value from API config
                                  if (value === 'percentage' || value === 'count') {
                                    const defaultValue = getDefaultValueForType(value);
                                    if (defaultValue !== undefined) {
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        idx,
                                        'threshold',
                                        defaultValue
                                      );
                                      // Also update local input value to reflect the change
                                      const rowErrorKey = getThresholdErrorKey(
                                        brandConfig.brandId,
                                        brandConfig.thresholds[idx].id
                                      );
                                      setLocalInputValues(prev => ({
                                        ...prev,
                                        [rowErrorKey]: String(defaultValue),
                                      }));
                                    }
                                  }

                                  // If changing to "updated_at" and connection is "ADD", change to "NONE"
                                  // (ADD operator requires same value type, updated_at doesn't work with ADD)
                                  if (isChangingToUpdatedAt && t.logicalOperator === 'ADD') {
                                    if (onUpdateLogicalOperator) {
                                      onUpdateLogicalOperator(brandConfig.brandId, idx, 'NONE');
                                    }
                                  }
                                });

                                // Track as auto-created if it matches a selected combination
                                if (
                                  threshold.taxonomyType &&
                                  selectedTaxonomyTypes.includes(threshold.taxonomyType) &&
                                  selectedValueTypes.includes(value) &&
                                  threshold.id
                                ) {
                                  const existingIds =
                                    autoCreatedThresholdsRef.current.get(newComboKey) || [];
                                  if (!existingIds.includes(threshold.id)) {
                                    autoCreatedThresholdsRef.current.set(newComboKey, [
                                      ...existingIds,
                                      threshold.id,
                                    ]);
                                  }
                                  autoCreatedCombinationsRef.current.add(newComboKey);
                                  // Remove from old combination tracking
                                  if (oldComboKey && oldComboKey !== newComboKey) {
                                    const oldIds =
                                      autoCreatedThresholdsRef.current.get(oldComboKey) || [];
                                    const updatedOldIds = oldIds.filter(id => id !== threshold.id);
                                    if (updatedOldIds.length === 0) {
                                      autoCreatedThresholdsRef.current.delete(oldComboKey);
                                      autoCreatedCombinationsRef.current.delete(oldComboKey);
                                    } else {
                                      autoCreatedThresholdsRef.current.set(
                                        oldComboKey,
                                        updatedOldIds
                                      );
                                    }
                                  }
                                }
                              }
                            }}
                            options={taxonomyValueTypeOptions}
                            placeholder="Select Value Type"
                            containerClassName="w-full"
                            maxWidth={270}
                          />
                        </div>
                        {selectedCell?.rowIndex === originalIndex &&
                          selectedCell?.column === 'valueType' &&
                          brandConfig.thresholds.length > 1 && (
                            <div
                              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                              style={{ transform: 'translate(50%, 50%)', borderRadius: '2px' }}
                              onMouseDown={e =>
                                handleFillHandleMouseDown(originalIndex, 'valueType', e)
                              }
                              title="Drag to fill cells above or below"
                            />
                          )}
                      </td>
                      {(() => {
                        // Calculate ADD chain information for operator column
                        const isPreviousRowAdd = prevThreshold?.logicalOperator === 'ADD';
                        const isCurrentRowAdd = threshold.logicalOperator === 'ADD';
                        let isInAddChain = false;
                        let addChainStart = originalIndex;
                        let addChainEnd = originalIndex;
                        let addChainLength = 1;

                        if (isPreviousRowAdd || isCurrentRowAdd) {
                          isInAddChain = true;

                          // Find the start of the ADD chain
                          addChainStart = originalIndex;
                          while (
                            addChainStart > 0 &&
                            brandConfig.thresholds[addChainStart - 1].logicalOperator === 'ADD'
                          ) {
                            addChainStart--;
                          }

                          // Find the end of the ADD chain
                          addChainEnd = originalIndex;
                          while (
                            addChainEnd < brandConfig.thresholds.length - 1 &&
                            brandConfig.thresholds[addChainEnd].logicalOperator === 'ADD'
                          ) {
                            addChainEnd++;
                          }

                          addChainLength = addChainEnd - addChainStart + 1;
                        }

                        // Calculate middle position
                        const middleIndex =
                          addChainLength % 2 === 0
                            ? addChainStart + addChainLength / 2 - 1
                            : addChainStart + Math.floor(addChainLength / 2);

                        const isMiddleRow = isInAddChain && originalIndex === middleIndex;

                        // Calculate rowSpan for even-numbered chains
                        const shouldSpanRows =
                          isInAddChain && isMiddleRow && addChainLength % 2 === 0;
                        const rowSpan = shouldSpanRows ? addChainLength + 1 : 1;

                        // If in ADD chain but not middle row, don't render the cell
                        const shouldSkipOperatorCell = isInAddChain && !isMiddleRow;

                        if (shouldSkipOperatorCell) {
                          return null;
                        }

                        return (
                          <td
                            ref={el => registerCellRef(originalIndex, 'operator', el)}
                            className={`${middleColBorderClass} px-2 py-2 cursor-pointer transition-all duration-150 ${
                              selectedCell?.rowIndex === originalIndex &&
                              selectedCell?.column === 'operator'
                                ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                                : ''
                            } ${isCellInFillRange(originalIndex, 'operator') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                            rowSpan={rowSpan}
                            style={{
                              verticalAlign: 'middle',
                              position: 'relative',
                              zIndex: isInAddChain ? 10 : 'auto',
                            }}
                            onClick={e => handleCellClick(originalIndex, 'operator', e)}
                            onMouseEnter={() => handleCellMouseEnter(originalIndex, 'operator')}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                              <div className="w-full">
                                <SingleSelect
                                  key={`${threshold.id}-operator-${threshold.operator}`}
                                  defaultValue={threshold.operator || '<'}
                                  disabled={isDisabled}
                                  onChange={(value: string) => {
                                    if (isInAddChain) {
                                      for (let i = addChainStart; i <= addChainEnd; i++) {
                                        onUpdateThreshold(
                                          brandConfig.brandId,
                                          i,
                                          'operator',
                                          value as '<' | '>' | '<=' | '>=' | '==' | '!='
                                        );
                                      }
                                    } else {
                                      onUpdateThreshold(
                                        brandConfig.brandId,
                                        originalIndex,
                                        'operator',
                                        value as '<' | '>' | '<=' | '>=' | '==' | '!='
                                      );
                                    }
                                  }}
                                  options={[
                                    { label: '<', value: '<' },
                                    { label: '>', value: '>' },
                                    { label: '<=', value: '<=' },
                                    { label: '>=', value: '>=' },
                                    { label: '==', value: '==' },
                                    { label: '!=', value: '!=' },
                                  ]}
                                  placeholder="Select Operator"
                                  containerClassName="w-full"
                                  maxWidth={120}
                                  clearIconClassName="hidden"
                                />
                              </div>
                            </div>
                            {selectedCell?.rowIndex === originalIndex &&
                              selectedCell?.column === 'operator' &&
                              brandConfig.thresholds.length > 1 && (
                                <div
                                  className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                                  style={{ transform: 'translate(50%, 50%)', borderRadius: '2px' }}
                                  onMouseDown={e =>
                                    handleFillHandleMouseDown(originalIndex, 'operator', e)
                                  }
                                  title="Drag to fill cells above or below"
                                />
                              )}
                          </td>
                        );
                      })()}
                      {(() => {
                        // Calculate ADD chain information for this row
                        const isPreviousRowAdd = prevThreshold?.logicalOperator === 'ADD';
                        const isCurrentRowAdd = threshold.logicalOperator === 'ADD';
                        let isInAddChain = false;
                        let addChainStart = originalIndex;
                        let addChainEnd = originalIndex;
                        let addChainLength = 1;

                        if (isPreviousRowAdd || isCurrentRowAdd) {
                          isInAddChain = true;

                          // Find the start of the ADD chain (go backwards)
                          addChainStart = originalIndex;
                          while (
                            addChainStart > 0 &&
                            brandConfig.thresholds[addChainStart - 1].logicalOperator === 'ADD'
                          ) {
                            addChainStart--;
                          }

                          // Find the end of the ADD chain (go forwards)
                          addChainEnd = originalIndex;
                          while (
                            addChainEnd < brandConfig.thresholds.length - 1 &&
                            brandConfig.thresholds[addChainEnd].logicalOperator === 'ADD'
                          ) {
                            addChainEnd++;
                          }

                          addChainLength = addChainEnd - addChainStart + 1;
                        }

                        // Calculate middle position
                        // For even number of rows (e.g., 2, 4), show on the first row of the second half
                        // For odd number of rows (e.g., 3, 5), show on the exact middle row
                        const middleIndex =
                          addChainLength % 2 === 0
                            ? addChainStart + addChainLength / 2 - 1 // For even, show on first row of second half
                            : addChainStart + Math.floor(addChainLength / 2); // For odd, show on exact middle

                        const isMiddleRow = isInAddChain && originalIndex === middleIndex;

                        // Calculate rowSpan for even-numbered chains
                        // For 2 rows: span 3 (threshold row + connector row + threshold row) to center it
                        // For 4 rows: span 5 (2 threshold rows + connector + 2 threshold rows)
                        // For odd-numbered chains: span 1 (just the middle row)
                        const shouldSpanRows =
                          isInAddChain && isMiddleRow && addChainLength % 2 === 0;
                        const rowSpan = shouldSpanRows ? addChainLength + 1 : 1; // +1 to include connector row

                        // If in ADD chain but not middle row, don't render the cell (it's covered by rowSpan)
                        const shouldSkipThresholdCell = isInAddChain && !isMiddleRow;

                        if (shouldSkipThresholdCell) {
                          return null;
                        }

                        const isUpdatedAtType = threshold.taxonomyValueType === 'updated_at';

                        return (
                          <td
                            ref={el => registerCellRef(originalIndex, 'threshold', el)}
                            className={`${middleColBorderClass} border-r-0 px-2 py-2 cursor-pointer transition-all duration-150 ${
                              selectedCell?.rowIndex === originalIndex &&
                              selectedCell?.column === 'threshold'
                                ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30'
                                : ''
                            } ${isCellInFillRange(originalIndex, 'threshold') ? 'bg-blue-100/50 ring-1 ring-blue-300 ring-inset' : ''}`}
                            rowSpan={rowSpan}
                            style={{
                              verticalAlign: 'middle',
                              position: 'relative',
                              zIndex: isInAddChain ? 10 : 'auto',
                            }}
                            onClick={e => handleCellClick(originalIndex, 'threshold', e)}
                            onMouseEnter={() => handleCellMouseEnter(originalIndex, 'threshold')}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                              {(() => {
                                if (isUpdatedAtType) {
                                  // For "updated_at" type, show date picker and time dropdowns
                                  // Parse threshold value as ISO date string or use current date
                                  let thresholdDate: Date;

                                  if (
                                    threshold.threshold &&
                                    typeof threshold.threshold === 'string'
                                  ) {
                                    // Try to parse as ISO date string
                                    const parsedDate = new Date(threshold.threshold);
                                    // Check if date is valid (not NaN and not 1970)
                                    if (
                                      !isNaN(parsedDate.getTime()) &&
                                      parsedDate.getFullYear() > 1970
                                    ) {
                                      thresholdDate = parsedDate;
                                    } else {
                                      // Invalid date, use current date
                                      thresholdDate = new Date();
                                    }
                                  } else {
                                    // No threshold or not a string, use current date
                                    thresholdDate = new Date();
                                  }

                                  const hours = Array.from({ length: 24 }, (_, i) => ({
                                    label: i.toString().padStart(2, '0'),
                                    value: i.toString(),
                                  }));

                                  const minutes = Array.from({ length: 60 }, (_, i) => ({
                                    label: i.toString().padStart(2, '0'),
                                    value: i.toString(),
                                  }));

                                  const currentHour = thresholdDate.getHours();
                                  const currentMinute = thresholdDate.getMinutes();

                                  return (
                                    <div className="w-full flex gap-2">
                                      <div className="flex-1">
                                        <DatePicker
                                          value={thresholdDate}
                                          onChange={(date: Date) => {
                                            if (date) {
                                              // Preserve time when date changes
                                              const newDate = new Date(date);
                                              newDate.setHours(currentHour);
                                              newDate.setMinutes(currentMinute);
                                              const isoString = newDate.toISOString();

                                              // If in ADD chain, update all rows in the chain
                                              if (isInAddChain) {
                                                for (let i = addChainStart; i <= addChainEnd; i++) {
                                                  onUpdateThreshold(
                                                    brandConfig.brandId,
                                                    i,
                                                    'threshold',
                                                    isoString
                                                  );
                                                }
                                              } else {
                                                onUpdateThreshold(
                                                  brandConfig.brandId,
                                                  originalIndex,
                                                  'threshold',
                                                  isoString
                                                );
                                              }
                                            }
                                          }}
                                          disabled={isDisabled}
                                        />
                                      </div>
                                      <div className="w-20">
                                        <SingleSelect
                                          key={`${threshold.id}-hour-${currentHour}`}
                                          defaultValue={currentHour.toString()}
                                          disabled={isDisabled}
                                          placeholder=""
                                          onChange={(value: string) => {
                                            const newDate = new Date(thresholdDate);
                                            newDate.setHours(parseInt(value));
                                            const isoString = newDate.toISOString();

                                            // If in ADD chain, update all rows in the chain
                                            if (isInAddChain) {
                                              for (let i = addChainStart; i <= addChainEnd; i++) {
                                                onUpdateThreshold(
                                                  brandConfig.brandId,
                                                  i,
                                                  'threshold',
                                                  isoString
                                                );
                                              }
                                            } else {
                                              onUpdateThreshold(
                                                brandConfig.brandId,
                                                originalIndex,
                                                'threshold',
                                                isoString
                                              );
                                            }
                                          }}
                                          options={hours}
                                          containerClassName="w-full"
                                          clearIconClassName="hidden"
                                        />
                                      </div>
                                      <div className="w-20">
                                        <SingleSelect
                                          key={`${threshold.id}-minute-${currentMinute}`}
                                          defaultValue={currentMinute.toString()}
                                          disabled={isDisabled}
                                          placeholder=""
                                          onChange={(value: string) => {
                                            const newDate = new Date(thresholdDate);
                                            newDate.setMinutes(parseInt(value));
                                            const isoString = newDate.toISOString();

                                            // If in ADD chain, update all rows in the chain
                                            if (isInAddChain) {
                                              for (let i = addChainStart; i <= addChainEnd; i++) {
                                                onUpdateThreshold(
                                                  brandConfig.brandId,
                                                  i,
                                                  'threshold',
                                                  isoString
                                                );
                                              }
                                            } else {
                                              onUpdateThreshold(
                                                brandConfig.brandId,
                                                originalIndex,
                                                'threshold',
                                                isoString
                                              );
                                            }
                                          }}
                                          options={minutes}
                                          containerClassName="w-full"
                                          clearIconClassName="hidden"
                                        />
                                      </div>
                                    </div>
                                  );
                                } else {
                                  // For "percentage" and "count" types, show number input
                                  const errorKey = getThresholdErrorKey(
                                    brandConfig.brandId,
                                    threshold.id
                                  );
                                  const localValue =
                                    localInputValues[errorKey] !== undefined
                                      ? localInputValues[errorKey]
                                      : threshold.threshold !== null &&
                                          threshold.threshold !== undefined
                                        ? String(threshold.threshold)
                                        : '';

                                  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                    const inputValue = e.target.value;
                                    // Mark as initialized to prevent external overwrites
                                    initializedKeysRef.current.add(errorKey);
                                    // Update local state immediately to allow clearing
                                    setLocalInputValues(prev => ({
                                      ...prev,
                                      [errorKey]: inputValue,
                                    }));

                                    // Only update threshold if it's a valid number
                                    if (inputValue !== '') {
                                      const numValue = parseFloat(inputValue);
                                      if (!isNaN(numValue)) {
                                        // If in ADD chain, update all rows in the chain
                                        if (isInAddChain) {
                                          for (let i = addChainStart; i <= addChainEnd; i++) {
                                            onUpdateThreshold(
                                              brandConfig.brandId,
                                              i,
                                              'threshold',
                                              numValue
                                            );
                                            // Update local values for all rows
                                            const rowErrorKey = getThresholdErrorKey(
                                              brandConfig.brandId,
                                              brandConfig.thresholds[i].id
                                            );
                                            initializedKeysRef.current.add(rowErrorKey);
                                            setLocalInputValues(prev => ({
                                              ...prev,
                                              [rowErrorKey]: inputValue,
                                            }));
                                          }
                                        } else {
                                          onUpdateThreshold(
                                            brandConfig.brandId,
                                            originalIndex,
                                            'threshold',
                                            numValue
                                          );
                                        }
                                      }
                                    }
                                  };

                                  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                                    const inputValue = e.target.value;
                                    const numValue = inputValue === '' ? 0 : parseFloat(inputValue);

                                    // Update threshold and local value
                                    if (inputValue === '') {
                                      // If in ADD chain, update all rows in the chain
                                      if (isInAddChain) {
                                        for (let i = addChainStart; i <= addChainEnd; i++) {
                                          onUpdateThreshold(brandConfig.brandId, i, 'threshold', 0);
                                          const rowErrorKey = getThresholdErrorKey(
                                            brandConfig.brandId,
                                            brandConfig.thresholds[i].id
                                          );
                                          setLocalInputValues(prev => ({
                                            ...prev,
                                            [rowErrorKey]: '0',
                                          }));
                                        }
                                      } else {
                                        onUpdateThreshold(
                                          brandConfig.brandId,
                                          originalIndex,
                                          'threshold',
                                          0
                                        );
                                        setLocalInputValues(prev => ({ ...prev, [errorKey]: '0' }));
                                      }
                                    } else if (!isNaN(numValue)) {
                                      setLocalInputValues(prev => ({
                                        ...prev,
                                        [errorKey]: String(numValue),
                                      }));
                                      // If in ADD chain, update all rows in the chain
                                      if (isInAddChain) {
                                        for (let i = addChainStart; i <= addChainEnd; i++) {
                                          onUpdateThreshold(
                                            brandConfig.brandId,
                                            i,
                                            'threshold',
                                            numValue
                                          );
                                          const rowErrorKey = getThresholdErrorKey(
                                            brandConfig.brandId,
                                            brandConfig.thresholds[i].id
                                          );
                                          setLocalInputValues(prev => ({
                                            ...prev,
                                            [rowErrorKey]: String(numValue),
                                          }));
                                        }
                                      } else {
                                        onUpdateThreshold(
                                          brandConfig.brandId,
                                          originalIndex,
                                          'threshold',
                                          numValue
                                        );
                                      }
                                    }

                                    // Validate and show error if needed
                                    // Skip max value validation for 'count' type
                                    const maxValueForType =
                                      threshold.taxonomyValueType === 'count'
                                        ? undefined
                                        : getMaxValueForType(threshold.taxonomyValueType);
                                    const validationError = validateThreshold(
                                      numValue,
                                      maxValueForType
                                    );
                                    if (validationError) {
                                      Toastify('error', validationError);
                                      setThresholdErrors(prev => ({
                                        ...prev,
                                        [errorKey]: validationError,
                                      }));
                                    } else {
                                      setThresholdErrors(prev => {
                                        const newErrors = { ...prev };
                                        delete newErrors[errorKey];
                                        return newErrors;
                                      });
                                    }
                                  };

                                  const isPercentageType =
                                    threshold.taxonomyValueType === 'percentage';

                                  return (
                                    <div className="w-full relative">
                                      <Input
                                        type="number"
                                        value={localValue}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter threshold"
                                        step="any"
                                        className="w-full"
                                        style={isPercentageType ? { paddingRight: '24px' } : {}}
                                      />
                                      {isPercentageType && (
                                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
                                          %
                                        </span>
                                      )}
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                            {selectedCell?.rowIndex === originalIndex &&
                              selectedCell?.column === 'threshold' &&
                              brandConfig.thresholds.length > 1 && (
                                <div
                                  className="absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-crosshair z-10 hover:bg-blue-700 transition-colors"
                                  style={{ transform: 'translate(50%, 50%)', borderRadius: '2px' }}
                                  onMouseDown={e =>
                                    handleFillHandleMouseDown(originalIndex, 'threshold', e)
                                  }
                                  title="Drag to fill cells above or below"
                                />
                              )}
                          </td>
                        );
                      })()}
                      {(() => {
                        // Calculate ADD chain information for Actions column (same logic as threshold column)
                        const isPreviousRowAdd = prevThreshold?.logicalOperator === 'ADD';
                        const isCurrentRowAdd = threshold.logicalOperator === 'ADD';
                        let isInAddChain = false;
                        let addChainStart = originalIndex;
                        let addChainEnd = originalIndex;
                        let addChainLength = 1;

                        if (isPreviousRowAdd || isCurrentRowAdd) {
                          isInAddChain = true;

                          // Find the start of the ADD chain (go backwards)
                          addChainStart = originalIndex;
                          while (
                            addChainStart > 0 &&
                            brandConfig.thresholds[addChainStart - 1].logicalOperator === 'ADD'
                          ) {
                            addChainStart--;
                          }

                          // Find the end of the ADD chain (go forwards)
                          addChainEnd = originalIndex;
                          while (
                            addChainEnd < brandConfig.thresholds.length - 1 &&
                            brandConfig.thresholds[addChainEnd].logicalOperator === 'ADD'
                          ) {
                            addChainEnd++;
                          }

                          addChainLength = addChainEnd - addChainStart + 1;
                        }

                        // Calculate middle position (same as threshold column)
                        const middleIndex =
                          addChainLength % 2 === 0
                            ? addChainStart + addChainLength / 2 - 1
                            : addChainStart + Math.floor(addChainLength / 2);

                        const isMiddleRow = isInAddChain && originalIndex === middleIndex;

                        // Calculate rowSpan for even-numbered chains (same as threshold column)
                        const shouldSpanRows =
                          isInAddChain && isMiddleRow && addChainLength % 2 === 0;
                        const rowSpan = shouldSpanRows ? addChainLength + 1 : 1; // +1 to include connector row

                        // If in ADD chain but not middle row, don't render the cell (it's covered by rowSpan)
                        const shouldSkipActionsCell = isInAddChain && !isMiddleRow;

                        if (shouldSkipActionsCell) {
                          return null;
                        }

                        return (
                          <td
                            className={`${lastColBorderClass} px-2 py-2 text-center`}
                            rowSpan={rowSpan}
                            style={{
                              verticalAlign: 'middle',
                              position: 'relative',
                              zIndex: isInAddChain ? 10 : 'auto',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                              }}
                            >
                              <button
                                onClick={() => {
                                  handleRemoveRow(originalIndex);
                                }}
                                disabled={brandConfig.thresholds.length === 1}
                                className={`${
                                  brandConfig.thresholds.length === 1
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : 'text-red-500 hover:text-red-700'
                                }`}
                              >
                                <TrashAlt className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        );
                      })()}
                    </SortableThresholdRow>
                  );
                })}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>

      <div className="flex justify-between mt-4">
        <Button variant="primary" onClick={() => onAddThresholdRow(brandConfig.brandId)}>
          Add Row
        </Button>
      </div>
    </div>
  );
};
